import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function OrgAdminLogin({ setUser }) {
  const [step, setStep] = useState(1); // 1: org ID, 2: login
  const [orgId, setOrgId] = useState('');
  const [org, setOrg] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    
    if (!orgId.trim()) {
      toast.error('請輸入機構編號');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations/${orgId}`);
      setOrg(res.data);
      setStep(2);
    } catch (error) {
      toast.error('機構編號不存在');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      const userData = res.data.user;
      
      // Strict role validation: only org_admin allowed
      if (userData.role !== 'org_admin') {
        localStorage.removeItem('token');
        const roleText = 
          userData.role === 'student' ? '學生' :
          userData.role === 'parent' ? '家長' :
          userData.role === 'coach' ? '教練' :
          userData.role === 'super_admin' ? '超級管理員' : '其他';
        toast.error(`此帳號是${roleText}帳號，請使用對應的登入頁面`);
        return;
      }
      
      // Case-insensitive comparison of organization_id
      if (userData.organization_id.toLowerCase() !== orgId.toLowerCase()) {
        toast.error('此帳號不屬於該機構');
        return;
      }

      localStorage.setItem('token', res.data.access_token);
      // Store the actual org_id from backend (not user input)
      localStorage.setItem('org_id', userData.organization_id);
      setUser(userData);
      toast.success('登入成功');
      // Redirect to admin dashboard with users tab as default
      navigate(`/org-settings/${userData.organization_id}`);
    } catch (error) {
      toast.error('用戶名稱或密碼錯誤');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-700">
      <div className="w-full max-w-md px-6">
        {step === 1 ? (
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-slate-800 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-800">機構管理員登入</h2>
              <p className="text-gray-600 mt-2">請輸入您的機構編號</p>
            </div>

            <form onSubmit={handleOrgSubmit} className="space-y-5">
              <div>
                <Input
                  data-testid="org-admin-org-id-input"
                  type="text"
                  placeholder="機構編號"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="h-12"
                />
              </div>

              <Button 
                data-testid="org-admin-continue-btn"
                type="submit" 
                className="w-full h-12 text-lg bg-slate-800 hover:bg-slate-900"
                disabled={loading}
              >
                {loading ? '驗證中...' : '繼續'}
              </Button>
            </form>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              {org?.logo_url && (
                <img src={org.logo_url} alt="Logo" className="h-16 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold text-gray-800">{org?.name}</h2>
              <p className="text-gray-600 mt-2">管理員登入</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Input
                  data-testid="org-admin-username-input"
                  type="text"
                  placeholder="管理員用戶名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12"
                />
              </div>

              <div>
                <Input
                  data-testid="org-admin-password-input"
                  type="password"
                  placeholder="密碼"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12"
                />
              </div>

              <Button 
                data-testid="org-admin-login-btn"
                type="submit" 
                className="w-full h-12 text-lg bg-slate-800 hover:bg-slate-900"
                disabled={loading}
              >
                {loading ? '登入中...' : '登入'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setStep(1)}
                className="text-sm text-gray-600 hover:underline"
              >
                返回
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
